import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Checkbox, FormControlLabel, 
    FormGroup, ListItem, ListItemIcon, ListItemText, Tooltip, 
    Button} from '@mui/material';
import { GridFormItem } from './GridForm';
import { Client, Field, Layout, Options, ParentField } from '../models/core';
import { Accordion, AccordionDetails, AccordionSummary } from './schema/Accordion';
import OptionsEditor from './schema/Options';
import { PatientFieldsList } from './schema/FieldList';
import { defaultLayout, id } from './schema/default';
import { API } from '../utils/Api';
import { Practice } from '../models/ziphy';
import EntityEditor, { EntityEditorProps } from './EntityEditor';
import { CheckCircleOutline } from '@mui/icons-material';
import RosterDialog from './patients/2_0/rosters/RosterDialog';
import { useAuth } from '../hooks/useAuth';


function addNewFields<T extends Field>(f1: T[], f2: T[]): T[] {
    return [...f1, ...f2.filter(f => f1.findIndex(ff => ff.field && ff.field == f.field || ff.defaultLabel == f.defaultLabel) < 0)];
}

function insertField<T extends Field>(fields: T[], newField: T, after: string, offset = 1) {
    if (fields.filter(f => f.field == newField.field).length == 0) {
        fields.splice(
            fields.findIndex(f => f.field == after) + offset, 0, newField
        );
    }
    return fields;
}

interface Props extends Omit<EntityEditorProps, 'title' | 'doSave'> {
    client?: Client;
    clientList?:Client[];
    duplicate?: boolean;
    practices: Practice[];
}

const ClientEditor = ({ client, practices, clientList, duplicate, ...props }: Props): JSX.Element => {

    const [practice, setPractice] = useState<Practice|null>((practices||[])?.find(e => e.id == client?.practiceId)|| null);
    const [sheetId, setSheetId] = useState(client?.sheetId || '');
    //const [msrFields, setMsrFields] = useState(addNewFields(client?.layout?.measureFields || [], defaultLayout.measureFields));
    //const [chkFields, setChkFields] = useState(addNewFields(client?.layout?.checkFields || [], defaultLayout.checkFields));
    const [patFields, setPatFields] = useState(addNewFields(client?.layout?.patientFields || [], defaultLayout.patientFields));
    const [vstFields, setVstFields] = useState(addNewFields(client?.layout?.visitFields || [], defaultLayout.visitFields));
    const [options, setOptions] = useState((client && client.layout || defaultLayout).options);
    const [errors, setErrors] = useState({ name: false, practiceId: false });
    const [rosterOpen, setRostersOpen] = useState<boolean>(false);

    const auth = useAuth();

    useEffect(() => {
        if (client) {
            setSheetId(client?.sheetId || '');
            //setMsrFields(addNewFields(client?.layout?.measureFields || [], defaultLayout.measureFields));
            //setChkFields(addNewFields(client?.layout?.checkFields || [], defaultLayout.checkFields));
            setPatFields(addNewFields(client?.layout?.patientFields || [], defaultLayout.patientFields));
            setVstFields(addNewFields(client?.layout?.visitFields || [], defaultLayout.visitFields));
            setOptions((client && client.layout || defaultLayout).options);
            setPractice((practices||[])?.find(e => e.id == client.practiceId)|| null);
        }
    }, [client, practices]);

    const layout = React.useMemo<Layout>(() => (
        { options, patientFields: patFields, measureFields: /*msrFields*/[], checkFields: /*chkFields*/[], visitFields: vstFields }
    ), [/*chkFields, msrFields,*/ options, patFields, vstFields]);

    /*const layoutJson = React.useMemo(() => (
        JSON.stringify(layout)
    ), [layout]);*/

    const onTextFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        switch (event.target.name) {
            // case 'name': setName(event.target.value); break;
            case 'sheetId': setSheetId(event.target.value); break;
            default:
                break;
        }
    }, []);

    const onPracticeChange = useCallback((_event: unknown, newValue: Practice | null) => {
        setPractice(newValue);
    }, []);

    const onOptionsChange = useCallback((newOptions: Options) => {
        setOptions(newOptions);
        if (newOptions.isMultiPlan) {
            setPatFields([...insertField(patFields, {
                label: 'Plan ID',
                defaultLabel: 'Plan ID',
                field: 'planId',
                id: id(),
                state: 'required',
                forChild: false
            }, 'refId')
            ]);
        }
        else {
            setPatFields(patFields.filter(f => f.field != 'planId'));
        }

        /*if (newOptions.checksSheet == newOptions.measuresSheet) {
            setChkFields([...insertField(msrFields, {
                label: 'Type',
                defaultLabel: 'Type',
                field: 'type',
                id: id(),
                state: 'required'
            }, 'name', 0)]);
            setMsrFields([...insertField(msrFields, {
                label: 'Type',
                defaultLabel: 'Type',
                field: 'type',
                id: id(),
                state: 'required'
            }, 'name', 0)]);
        }
        else {
            setChkFields(msrFields.filter(f => f.field != 'type'));
            setMsrFields(msrFields.filter(f => f.field != 'type'));
        }*/

        /*if (newOptions.visitsSheet) {
            const t = insertField(msrFields, {
                label: 'Visit ID',
                defaultLabel: 'Visit ID',
                field: 'visitId',
                id: id(),
                state: 'required'
            }, 'status');
            setMsrFields([...insertField(t, {
                label: 'Visit Date',
                defaultLabel: 'Visit Date',
                field: 'visitDate',
                id: id(),
                state: 'required'
            }, 'visitId')]);
        }
        else {
            setMsrFields(msrFields.filter(f => !['visitId', 'visitDate'].includes(f.field || '')));
        }*/
    }, [/*msrFields,*/ patFields]);

    const patFieldFactory = useCallback(() => {
        const numCustomFields = patFields.filter(f => !f.field).length;
        return { id: id(), label: `Custom Field ${numCustomFields + 1}`, state: 'enabled', type: 'string', forChild: false, isCustom:true } as ParentField;
    }, [patFields]);

    const onSubmit = useCallback(async () => {
        const errors = {
            name: !(practice?.name),
            practiceId:!(practice?.id),
            //sheetId: !sheetId
        };
        setErrors(errors);
        const errorData = Object.values(errors).reduce((acc, val) => acc || val);
        if (Object.values(errors).reduce((acc, val) => acc || val)) {
            console.error('Errors Found: ' + errorData)
            return false;
        }
        const data: Omit<Client, '_id'> = {
            name: practice?.name || '',
            sheetId,
            isMultiPlan: false, // TODO remove
            practiceId: practice?.id || 0,
            layout
        };
        if (client && !duplicate) {
            await API.updateClient({ ...client, ...data })
        }
        else {
            await API.createClient(data);
        }
        return true;
    }, [client, duplicate, layout, practice, sheetId]);

    return <>
        <EntityEditor {...props} title={client ? 'Edit Client' : 'New Client'} doSave={onSubmit}>
            <GridFormItem xs={12}>
                <Autocomplete
                    options={practices}
                    value={practice}
                    onChange={onPracticeChange}
                    autoHighlight
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            name='name'
                            label='Name'
                            // value={name || ''}
                            // onChange={onTextFieldChange}
                            variant='standard'
                            fullWidth
                            required
                            error={errors.name}
                            helperText={errors.name && 'Field is required'}
                        />
                    }
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <ListItem disablePadding>
                                {(clientList || []).find(e => e.practiceId == option.id) && (
                                    <Tooltip title='Exists in Outreach'>
                                        <span>
                                            <ListItemIcon>
                                                <CheckCircleOutline color="success" />
                                            </ListItemIcon>
                                        </span>
                                    </Tooltip>
                                )}
                                <ListItemText primary={option.name} />
                            </ListItem>
                        </li>
                    )}
                    getOptionLabel={(option) => option.name || 'unlabeled'}
                />
            </GridFormItem>

            <GridFormItem xs={4} sx={{ display: 'flex', alignItems: 'baseline' }}>
                <TextField
                    name='practiceId'
                    label='Practice ID'
                    value={practice?.id || ''}
                    onChange={onTextFieldChange}
                    variant='standard'
                    disabled={true}
                    fullWidth
                />
            </GridFormItem>
            <GridFormItem xs={4} >
                <FormGroup>
                    <FormControlLabel control={<Checkbox disabled checked={practice?.demo || false}/>} label="Demo" />
                </FormGroup>
            </GridFormItem>
            <GridFormItem xs={4} >
                <FormGroup>
                    <FormControlLabel control={<Checkbox disabled checked={practice?.is_active || false}/>} label="Active" />
                </FormGroup>
            </GridFormItem>
            <input type='hidden' name='practiceId' value={practice?.id || ''} />
            {/*<input type='hidden' name='layout' value={layoutJson} />*/}
            <GridFormItem xs={12}>
                <Box sx={{ paddingRight: '16px' }}>
                    <Accordion>
                        <AccordionSummary>Client Options</AccordionSummary>
                        <AccordionDetails sx={{ padding: '16px' }}>
                            <OptionsEditor value={options} onChange={onOptionsChange} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary>Patient Fields</AccordionSummary>
                        <AccordionDetails>
                            <PatientFieldsList fields={patFields} factory={patFieldFactory} onChange={setPatFields} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary>Visit Fields</AccordionSummary>
                        <AccordionDetails>
                            <PatientFieldsList fields={vstFields} factory={patFieldFactory} onChange={setVstFields} />
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </GridFormItem>
            <GridFormItem xs={12}>
                <Button onClick={()=>setRostersOpen(true)}>Open Import Roster</Button>
            </GridFormItem>
            { client && auth?.role == 'admin' && rosterOpen ? <RosterDialog open={rosterOpen}
                onClose={() =>{setRostersOpen(false);}} client={client}/> : <></> }
        </EntityEditor >
    </>
}

export default ClientEditor;
